import type { TenantSubscription, BillingPlan } from '@yawi/types';
import { Badge, Button, Card } from '@/core/ui/components';
import { CreditCard, Edit, XCircle } from 'lucide-react';

const STATUS_VARIANTS: Record<string, 'success' | 'warning' | 'error' | 'default' | 'purple'> = {
  ACTIVE: 'success',
  TRIAL: 'purple',
  PAST_DUE: 'warning',
  SUSPENDED: 'error',
  CANCELLED: 'default',
};

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Activo',
  TRIAL: 'Prueba',
  PAST_DUE: 'Pago pendiente',
  SUSPENDED: 'Suspendido',
  CANCELLED: 'Cancelado',
};

interface TenantSubscriptionCardProps {
  subscription?: TenantSubscription | null;
  plan?: BillingPlan | null;
  onEdit: () => void;
  onCancel: () => void;
}

export function TenantSubscriptionCard({ subscription, plan, onEdit, onCancel }: TenantSubscriptionCardProps) {
  if (!subscription) {
    return (
      <Card>
        <div className="flex items-center gap-3 mb-4">
          <CreditCard className="w-5 h-5 text-theme-secondary" />
          <h3 className="text-lg font-semibold text-theme">Suscripción</h3>
        </div>
        <div className="text-center py-8">
          <p className="text-theme-secondary">No hay suscripción activa</p>
          <Button className="mt-4" onClick={onEdit}>
            Crear suscripción
          </Button>
        </div>
      </Card>
    );
  }

  const planName = subscription.plan?.name ?? plan?.name ?? 'Plan';
  const formatDate = (d: string) => new Date(d).toLocaleDateString('es-MX', {
    year: 'numeric', month: 'short', day: 'numeric',
  });

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <CreditCard className="w-5 h-5 text-theme-secondary" />
          <h3 className="text-lg font-semibold text-theme">Suscripción</h3>
        </div>
        <Badge variant={STATUS_VARIANTS[subscription.status] || 'default'}>
          {STATUS_LABELS[subscription.status] || subscription.status}
        </Badge>
      </div>

      <dl className="space-y-3">
        <div className="flex justify-between">
          <dt className="text-theme-secondary text-sm">Plan</dt>
          <dd className="text-theme font-medium">{planName}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-theme-secondary text-sm">Ciclo</dt>
          <dd className="text-theme">{subscription.billingCycle === 'MONTHLY' ? 'Mensual' : 'Anual'}</dd>
        </div>
        {subscription.billingCycleStart && (
          <div className="flex justify-between">
            <dt className="text-theme-secondary text-sm">Inicio del ciclo</dt>
            <dd className="text-theme">{formatDate(subscription.billingCycleStart)}</dd>
          </div>
        )}
        <div className="flex justify-between">
          <dt className="text-theme-secondary text-sm">Próxima facturación</dt>
          <dd className="text-theme">{formatDate(subscription.nextBillingDate)}</dd>
        </div>
        {subscription.trialEndsAt && (
          <div className="flex justify-between">
            <dt className="text-theme-secondary text-sm">Fin de prueba</dt>
            <dd className="text-theme text-purple-600">{formatDate(subscription.trialEndsAt)}</dd>
          </div>
        )}
        {subscription.activeModules && subscription.activeModules.length > 0 && (
          <div className="flex justify-between items-start">
            <dt className="text-theme-secondary text-sm">Módulos</dt>
            <dd className="flex flex-wrap gap-1 justify-end">
              {subscription.activeModules.map((m) => (
                <Badge key={m} variant="default">{m}</Badge>
              ))}
            </dd>
          </div>
        )}
        {subscription.notes && (
          <div className="flex justify-between items-start">
            <dt className="text-theme-secondary text-sm">Notas</dt>
            <dd className="text-theme text-sm max-w-xs text-right">{subscription.notes}</dd>
          </div>
        )}
      </dl>

      <div className="flex gap-2 mt-6 pt-4 border-t border-theme">
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          leftIcon={<Edit className="w-4 h-4" />}
          onClick={onEdit}
        >
          Editar
        </Button>
        {subscription.status !== 'CANCELLED' && (
          <Button
            variant="outline"
            size="sm"
            className="text-red-600 border-red-200 hover:bg-red-50 dark:hover:bg-red-900/20"
            leftIcon={<XCircle className="w-4 h-4" />}
            onClick={onCancel}
          >
            Cancelar
          </Button>
        )}
      </div>
    </Card>
  );
}
